// sm	640px
// md	768px
// lg	1024px
// xl	1280px
// 2xl	1536px

import { Injectable, inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { fromEvent, startWith, switchMap, tap } from 'rxjs';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { isFnBApp } from '@us/web/shared/utils';
import { ENVIRONMENT } from '@us/app-config';

interface ResponsiveState {
    breakpoint: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    index_count: number;
    width_category: 'width-xs' | 'width-sm' | 'width-md' | 'width-lg' | 'width-xl' | 'width-xll';
}

@Injectable({
    providedIn: 'root'
})
export class ResponsiveStore extends ComponentStore<ResponsiveState> {
    private readonly env = inject(ENVIRONMENT);
    readonly isFnBApp = isFnBApp(this.env);
    constructor() {
        super({
            breakpoint: 'xs',
            index_count: 5,
            width_category: 'width-xs'
        });
    }

    readonly breakpoint$ = fromEvent(window, 'resize').pipe(
        startWith(null),
        tap(() => {
            const width = window.innerWidth;
            if (width >= 1536) {
                this.updateBreakpoint('2xl');
            } else if (width >= 1280) {
                this.updateBreakpoint('xl');
            } else if (width >= 1024) {
                this.updateBreakpoint('lg');
            } else if (width >= 768) {
                this.updateBreakpoint('md');
            } else if (width >= 640) {
                this.updateBreakpoint('sm');
            } else {
                this.updateBreakpoint('xs');
            }
        }),
        switchMap(() => this.select(state => state.breakpoint))
    );

    readonly indexCount$ = fromEvent(window, 'resize').pipe(
        startWith(null),
        tap(() => {
            const width = window.innerWidth;
            if (width >= 1536) {
                this.updateIndexCount(8);
            } else if (width >= 1280) {
                this.updateIndexCount(7);
            } else if (width >= 1024) {
                this.updateIndexCount(6);
            } else if (width >= 768) {
                this.updateIndexCount(5);
            } else if (width >= 640) {
                this.updateIndexCount(4);
            } else {
                this.updateIndexCount(3);
            }
        }),
        switchMap(() => this.select(state => state.index_count))
    );

    readonly widthCategory$ = fromEvent(window, 'resize').pipe(
        startWith(null),
        tap(() => {
            const width = window.innerWidth;
            if (width >= 1536) {
                this.updateWidthCategory('width-xll');
            } else if (width >= 1280) {
                this.updateWidthCategory('width-xl');
            } else if (width >= 1024) {
                this.updateWidthCategory('width-lg');
            } else if (width >= 768) {
                this.updateWidthCategory('width-md');
            } else if (width >= 640) {
                this.updateWidthCategory('width-sm');
            } else {
                this.updateWidthCategory('width-xs');
            }
        }),
        switchMap(() => this.select(state => state.width_category))
    );

    readonly updateBreakpoint = this.updater(
        (state, breakpoint: ResponsiveState['breakpoint']): ResponsiveState => ({
            ...state,
            breakpoint
        })
    );
    readonly updateIndexCount = this.updater(
        (state, index_count: ResponsiveState['index_count']): ResponsiveState => ({
            ...state,
            index_count
        })
    );

    readonly updateWidthCategory = this.updater(
        (state, width_category: ResponsiveState['width_category']): ResponsiveState => ({
            ...state,
            width_category
        })
    );
}
